<template>
    <div class="forgot-container">
        <div class="forgot-container__title">找回密码</div>
        <el-form
            class="forgot-container__form"
            ref="forgotPassFormRef"
            :model="forgotPassForm"
            :rules="forgotPassFormRules"
        >
            <div class="forgot-container__phone">
                <el-select
                    v-model="areaCode"
                    placeholder="请选择"
                    class="forgot-container__phone--select"
                >
                    <el-option
                        v-for="item in areaCodeData"
                        :key="item.label"
                        :label="item.label"
                        :value="item.value"
                    ></el-option>
                </el-select>
                <el-form-item prop="account">
                    <el-input
                        class="forgot-container__phone--input"
                        v-model="forgotPassForm.account"
                        placeholder="输入手机号"
                        name="account"
                        type="text"
                        maxlength="20"
                        clearable
                    />
                </el-form-item>
            </div>

            <el-form-item prop="verificationCode" class="forgot-container__indentify">
                <el-input
                    class="forgot-container__indentify--input"
                    type="text"
                    name="verificationCode"
                    maxlength="6"
                    placeholder="输入6位短信验证码"
                    v-model="forgotPassForm.verificationCode"
                    clearable
                ></el-input>
                <el-button
                    size="small"
                    type="primary"
                    @click="handleSendIdentifyCode"
                    :disabled="codeCountDown_IsRunning"
                >
                    <p>{{codeCountDown_IsRunning ? `${codeCountDown_Num}秒后重新获取` : identifyBtnText}}</p>
                </el-button>
            </el-form-item>

            <el-form-item prop="password">
                <el-input
                    :key="passwordType"
                    class="forgot-container__password--input"
                    ref="passwordRef"
                    v-model="forgotPassForm.password"
                    :type="passwordType"
                    placeholder="输入密码"
                    name="password"
                    clearable
                    @keyup.enter="handleSubmit"
                ></el-input>
                <span class="show-pwd" @click="showPwd">
                    <svg-icon :name="passwordType === 'password' ? 'eye-off' : 'eye-on'" />
                </span>
            </el-form-item>
        </el-form>
        <el-button
            class="forgot-container__btn"
            :loading="loading"
            :disabled="btnDisabled"
            type="primary"
            @click="handleSubmit"
        >提交</el-button>
    </div>
</template>

<script lang='ts'>
import useCountDown from '@/composable/useCountDown'
import { useStore } from '@/store'
import { UserActionTypes } from '@/store/modules/user/action-types'
import { isValidPhone } from '@/utils/validate'
import { ElMessage } from 'element-plus/lib/components'
import { defineComponent, nextTick, reactive, ref, toRefs } from 'vue'
import { ZoneCode } from './zoneCode'

interface IForgotPassState {
  loading: boolean
  btnDisabled: boolean
  forgotPassForm: {
    account: string
    verificationCode: string
    password: string
  }
  identifyBtnText: string
  areaCodeData: {
    label: string
    value: number
  }[]
  areaCode: number | string
  passwordType: string
  forgotPassFormRules: object
}
export default defineComponent({
  name: 'ForgotPass',
  setup(props, { emit }) {
    const state = reactive<IForgotPassState>({
      loading: false,
      btnDisabled: false,
      forgotPassForm: {
        account: '',
        verificationCode: '',
        password: ''
      },
      identifyBtnText: '获取验证码',
      areaCodeData: ZoneCode,
      areaCode: ZoneCode[0].value,
      passwordType: 'password',
      forgotPassFormRules: {
        account: [
          {
            required: true,
            pattern: /^\d{5,20}$/,
            message: '请核对手机号是否正确',
            trigger: 'blur'
          }
        ],
        password: [
          {
            required: true,
            pattern: /^.{6,20}$/,
            message: '请输入6-20位密码',
            trigger: 'change'
          },
          { min: 6, max: 20, message: '长度6-20个字符', trigger: 'blur' }
        ],
        verificationCode: [
          {
            required: true,
            pattern: /^\d{6,6}$$/,
            message: '验证码格式不通过',
            trigger: 'blur'
          }
        ]
      }
    })
    const store = useStore()
    const passwordRef = ref<any>(null)
    const forgotPassFormRef = ref()
    const [codeCountDown_Num, codeCountDown_IsRunning, codeCountDown_Start] =
      useCountDown(60)

    /** 控制密码显示 */
    const showPwd = () => {
      if (state.passwordType === 'password') {
        state.passwordType = ''
      } else {
        state.passwordType = 'password'
      }

      nextTick(() => {
        passwordRef.value.focus()
      })
    }

    /** 点击获取验证码 */
    const handleSendIdentifyCode = () => {
      if (!isValidPhone(state.forgotPassForm.account)) {
        ElMessage.warning('请核对手机号码是否正确！！！')
        return
      }
      codeCountDown_IsRunning.value = true
      getIdentifyCode()
    }

    /** 发送验证码请求 */
    const getIdentifyCode = () => {
      const params = {
        phone: state.forgotPassForm.account,
        type: 2, // 0通用，1注册， 2登录，3修改密码
        areaCode: state.areaCode, // 手机区号 （国内可不填）
        pf: 3,
        version: 'web',
        evkAppId: '10009'
      }
      store
        .dispatch(UserActionTypes.ACTION_GET_IDENTIFY_CODE, params)
        .then((res: any) => {
          const errInfo = res.data.errInfo
          if (res.status === 1) {
            ElMessage.success('验证码发送成功')
            codeCountDown_Start()
          } else if (errInfo) {
            ElMessage.error(errInfo)
            setTimeout(() => {
              state.identifyBtnText = '重新获取'
              codeCountDown_IsRunning.value = true
            }, 2000)
          }
        })
        .catch((err) => {
          setTimeout(() => {
            state.identifyBtnText = '重新获取'
            codeCountDown_IsRunning.value = true
          }, 2000)
          console.error(err)
        })
    }

    /** 提交 */
    function handleSubmit() {
      forgotPassFormRef.value.validate((valid: boolean) => {
        if (valid) {
          state.btnDisabled = true
          state.loading = true
          const params = {
            pf: 3, // 登录平台 1:android 2:ios 3:web 4:pc
            version: 'web', // 客户端版本号
            phone: state.forgotPassForm.account,
            verificationCode: state.forgotPassForm.verificationCode, // 验证码
            areaCode: state.areaCode, // 手机区号 （国内可不填）
            newPwd: state.forgotPassForm.password
          }
          store
            .dispatch(UserActionTypes.ACTION_CODE_UPDATE_Pwd, params)
            .then((res: any) => {
              if (res.data.errInfo) {
                ElMessage.error(res.data.errInfo.message)
                return
              }
              initStatus()
              ElMessage.success('密码更改成功,请重新登录吧！！！')
              emit('success')
            })
            .catch((error: any) => {
              handleLoginFailure(error)
            })
        }
      })
    }

    const initStatus = () => {
      state.loading = false
      state.btnDisabled = false
      codeCountDown_IsRunning.value = false
      state.passwordType = 'password'
      state.forgotPassForm.password = ''
      state.forgotPassForm.verificationCode = ''
    }

    const handleLoginFailure = (error: any) => {
      setTimeout(() => {
        state.loading = false
        state.btnDisabled = false
      }, 2000)
      let info =
        error.errInfo && error.errInfo.message
          ? error.errInfo.message
          : '服务器错误'
      ElMessage.error(info)
    }

    return {
      showPwd,
      codeCountDown_Num,
      codeCountDown_IsRunning,
      passwordRef,
      forgotPassFormRef,
      ...toRefs(state),
      handleSendIdentifyCode,
      handleSubmit
    }
  }
})
</script>
<style lang='scss' scoped>
@include b(forgot-container) {
  text-align: center;

  @include e(title) {
    text-align: center;
    font-size: 22px;
    font-weight: 400;
    color: rgba(36, 39, 65, 1);
    line-height: 30px;
    margin-bottom: 40px;
  }

  @include e(phone) {
    display: flex;
    justify-content: center;
    @include m(select) {
      width: 90px;
      margin-right: 3px;
      :deep(.el-input__inner) {
        border: none;
        border-bottom: 1px solid #efefef;
        border-radius: 0;
        &:focus {
          border-bottom: 1px solid #242741;
        }
      }
    }
    @include m(input) {
      width: 140px;
    }
  }

  @include e(indentify) {
    display: flex;
    @include m(input) {
      width: 140px;
      margin-bottom: 5px;
    }
  }

  @include e(btn) {
    margin-top: 30px;
    width: 180px;
    height: 48px;
    background: #ababab;
    box-shadow: 0px 11px 33px -6px rgba(130, 140, 153, 0.68),
      0px 2px 8px -4px #d8dbe0;
    border-radius: 4px;
    margin-bottom: 120px;
    border: none;
    font-size: 18px;
    color: #ffffff;

    &:hover {
      color: #272727;
      background: #ffc400;
    }
    &:focus {
      color: #272727;
      background: #ffc400;
    }
  }
}

.forgot-container__phone--input :deep(.el-input__inner) {
  @include login-input;
  &:focus {
    border-bottom: 1px solid #242741;
  }
}

.forgot-container__indentify--input :deep(.el-input__inner) {
  @include login-input;
  &:focus {
    border-bottom: 1px solid #242741;
  }
}

.forgot-container__password--input {
  width: 225px;
  margin-bottom: 5px;
}

.forgot-container__password--input :deep(.el-input__inner) {
  @include login-input;
  &:focus {
    border-bottom: 1px solid #242741;
  }
}
.show-pwd {
  font-size: 16px;
  color: #889aa4;
  cursor: pointer;
  user-select: none;
}
</style>
